import React, { useState, useRef } from "react";
import { MdCloudUpload } from "react-icons/md";
import { toast } from "react-toastify";
import { adminApiService } from "services/api";

export default function PageVideoModal({
  isOpen,
  onClose,
  onSubmit,
  categoryId,
}) {
  const [pageVideo, setPageVideo] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith("video/")) {
        setPageVideo(file);
        // Create preview URL
        const videoUrl = URL.createObjectURL(file);
        setPreviewUrl(videoUrl);
      } else {
        toast.error("Please select a valid video file");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pageVideo) {
      toast.error("Please select a video file");
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("page_video", pageVideo);
      formData.append("showInMenu", "true");

      const response = await adminApiService.updateMenuCategoryStatus(
        categoryId,
        formData
      );

      if (!response.ok) throw new Error("Failed to update category");

      toast.success("Category updated successfully!");
      onSubmit(categoryId);
      setPageVideo(null);
      setPreviewUrl("");
      onClose();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Error updating category");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("video/")) {
      setPageVideo(file);
      const videoUrl = URL.createObjectURL(file);
      setPreviewUrl(videoUrl);
    } else {
      toast.error("Please drop a valid video file");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">
          Upload Page Video
        </h2>
        <form onSubmit={handleSubmit}>
          <div
            className="mb-4 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-6 hover:border-primary"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="video/*"
              className="hidden"
            />
            {previewUrl ? (
              <div className="w-full">
                <video
                  src={previewUrl}
                  className="mb-2 max-h-48 w-full rounded-lg object-cover"
                  controls
                />
                <p className="text-center text-sm text-gray-500">
                  {pageVideo.name}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <MdCloudUpload className="mb-2 text-4xl text-gray-400" />
                <p className="text-center text-sm text-gray-500">
                  Drag and drop a video file here, or click to select
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  Supported formats: MP4, WebM, MOV
                </p>
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading || !pageVideo}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isUploading ? "Uploading..." : "Update"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
